import {
  Alert,
  AuthLayout,
  FooterLinks,
  SSOButton,
  StatusDot,
  useBrand,
  type SSOProvider,
} from '@hkdevs/design-system';
import { useState } from 'react';
import type { Tenant } from '../api/queries/tenant';
import './Login.css';

export type LoginError = 'not_authorized' | 'provider_error' | 'link_expired';

// The prototype's copy (HK Payments Login.dc.html) for the four provider states.
const SUBTITLES = {
  google: "Use your organization's Google account to continue.",
  microsoft: "Use your organization's Microsoft account to continue.",
  both: "Choose your organization's identity provider to continue.",
  none: 'Access to your payments workspace requires single sign-on.',
};

function errorAlert(error: LoginError, productName: string, canSignIn: boolean) {
  switch (error) {
    case 'not_authorized':
      return {
        title: "We couldn't sign you in",
        body: `Your account isn't set up for ${productName}. Ask your administrator to add you, then try again.`,
      };
    case 'provider_error':
      return {
        title: "We couldn't sign you in",
        body: "The sign-in didn't complete. Try again, or contact your administrator if it keeps happening.",
      };
    case 'link_expired':
      // A partner's login link (ADR 0031). Generic: Login doesn't know which site sent it.
      return {
        title: 'That sign-in link has expired',
        body: `Go back to the site you came from and try again${canSignIn ? ', or sign in below.' : '.'}`,
      };
  }
}

export interface LoginProps {
  tenant: Tenant;
  error?: LoginError | undefined;
  /**
   * Starts a sign-in: the app navigates to `href`, /api/auth/login/{provider} or a
   * partner's /api/auth/partner/{id}/login (ADR 0031).
   */
  onSignIn: (href: string) => void;
}

/** A sign-in button: Google, Microsoft, then the tenant's partners, in its order. */
interface SignInOption {
  href: string;
  provider: SSOProvider;
  partnerName?: string;
}

/**
 * The Login screen: the design system's Login composition (pc-of9.27). The brand (names,
 * logo, secure host) comes from the ThemeRoot; the providers and links from the tenant.
 */
export function Login({ tenant, error, onSignIn }: LoginProps) {
  // The href of the sign-in under way.
  const [signingIn, setSigningIn] = useState<string>();
  const { brandName, productName } = useBrand();

  const providers = (['google', 'microsoft'] as const).filter(
    (provider) => tenant.providers[provider],
  );
  const options: SignInOption[] = [
    ...providers.map((provider) => ({ href: `/api/auth/login/${provider}`, provider })),
    ...tenant.partners.map((partner) => ({
      href: `/api/auth/partner/${encodeURIComponent(partner.id)}/login`,
      provider: 'partner' as const,
      partnerName: partner.name,
    })),
  ];
  const canSignIn = options.length > 0;
  const subtitle =
    providers.length === 2
      ? SUBTITLES.both
      : providers.length === 1
        ? SUBTITLES[providers[0]!]
        : SUBTITLES.none;

  const links = [
    { label: 'Help', href: tenant.helpLinks.help },
    { label: 'Privacy', href: tenant.helpLinks.privacy },
    { label: 'Terms', href: tenant.helpLinks.terms },
  ].filter((link): link is { label: string; href: string } => link.href !== null);

  function signIn(href: string) {
    setSigningIn(href);
    onSignIn(href);
  }

  // A sign-in error only matters with a way to retry; an expired partner link always
  // does, since its user may have no other way in.
  const alert =
    error && (canSignIn || error === 'link_expired')
      ? errorAlert(error, productName, canSignIn)
      : undefined;

  return (
    <AuthLayout
      title={`Sign in to ${productName}`}
      subtitle={subtitle}
      footnote={
        canSignIn ? `Single sign-on secured by ${brandName} · no password required` : undefined
      }
      footer={links.length > 0 ? <FooterLinks links={links} /> : undefined}
    >
      {alert && (
        <Alert tone="danger" title={alert.title}>
          {alert.body}
        </Alert>
      )}
      {options.map(({ href, provider, partnerName }) => (
        <SSOButton
          key={href}
          provider={provider}
          {...(partnerName ? { partnerName } : {})}
          loading={signingIn === href}
          disabled={signingIn !== undefined && signingIn !== href}
          onClick={() => signIn(href)}
        />
      ))}
      {!canSignIn && (
        <>
          <Alert tone="warning" title="Sign-in temporarily unavailable">
            No sign-in providers are currently enabled for {productName}. This is usually brief —
            please try again in a few minutes, or contact your administrator if it persists.
          </Alert>
          <StatusDot tone="warning" className="pc-login__status">
            Status: providers offline
          </StatusDot>
        </>
      )}
    </AuthLayout>
  );
}
